import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useTranslation } from "react-i18next";
import BookingApi from "../../API/Apis/BookingApi";
import { ShareboxPickupResponse } from "../../@types/DTO/Sharebox";
import BookingGetters from "../../store/onlineBooking/getters";
import ShareboxHeader from "./components/ShareboxHeader";
import ShareboxDetailCard from "./components/ShareboxDetailCard";
import ShareboxFooter from "./components/ShareboxFooter";

type Props = {
  token: string;
  pickup: ShareboxPickupResponse;
  onNext: () => void;
  onBack?: () => void;
};

export default function D6ContractReview({
  token,
  pickup,
  onNext,
  onBack,
}: Props) {
  const { t: translate } = useTranslation();
  const appSettings = BookingGetters.getAppSettings();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  const handleDownloadContract = async () => {
    if (!token) return;
    setDownloading(true);
    setDownloadError("");
    try {
      const response = await BookingApi.getShareboxContractDownloadUrl(token);
      const url = response.data.url;
      if (!url) {
        setDownloadError(
          translate("The contract isn't ready to download yet.")
        );
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
      try {
        const pdfResponse = await fetch(url);
        const blob = await pdfResponse.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `contract-${pickup.reservationCode}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (blobErr) {
        console.error(
          "Sharebox contract: blob-fetch download failed (new tab still opened)",
          blobErr
        );
      }
    } catch (err: any) {
      console.error("Sharebox contract: failed to get download link", err);
      setDownloadError(
        err?.response?.data?.reason ||
          translate("Unable to download the contract. Please try again.")
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleContinue = async () => {
    setSubmitting(true);
    setError("");
    try {
      if (token) {
        await BookingApi.acceptShareboxContract(token);
        await BookingApi.sendShareboxOtpEmail(token);
      }
      onNext();
    } catch (err: any) {
      console.log(err, "error");
      setError(
        err?.response?.data?.reason ||
          translate("Something went wrong. Please try again.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.paper" }}>
      <ShareboxHeader
        logoUrl={appSettings?.config?.logoUrl}
        rightSlot={`${translate("Step")} 2 ${translate("of")} 3`}
        onBack={onBack}
      />

      <Box sx={{ maxWidth: 480, mx: "auto", px: 3, py: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {translate("Your rental contract")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {translate("Review the summary below.")}
        </Typography>

        <Link
          component="button"
          type="button"
          onClick={handleDownloadContract}
          underline="hover"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            mb: downloadError ? 1 : 3,
          }}
        >
          {downloading ? (
            <CircularProgress size={16} />
          ) : (
            <PictureAsPdfIcon fontSize="small" />
          )}
          {translate("The full PDF is available here")}
        </Link>
        {downloadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {downloadError}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <ShareboxDetailCard
            rows={[
              [translate("Driver"), pickup.booking.renterName],
              [
                translate("Vehicle"),
                pickup.booking.plate
                  ? `${pickup.booking.vehicleDescription} — ${pickup.booking.plate}`
                  : pickup.booking.vehicleDescription,
              ],
              [
                translate("Pickup"),
                new Date(pickup.booking.pickupAt).toLocaleString(undefined, {
                  timeZone: "UTC",
                }),
              ],
              [
                translate("Drop-off"),
                new Date(pickup.booking.dropoffAt).toLocaleString(undefined, {
                  timeZone: "UTC",
                }),
              ],
            ]}
          />
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
            />
          }
          label={translate("I have read and accept the rental terms")}
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <Button
          variant="contained"
          fullWidth
          className="webCheckInButton"
          disabled={!accepted || submitting}
          onClick={handleContinue}
          endIcon={submitting ? undefined : <ArrowForwardIcon />}
          sx={{
            mt: 3,
            width: "100% !important",
            maxWidth: "none !important",
            color: "#fff",
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} />
              {translate("Sending OTP...")}
            </>
          ) : (
            translate("Sign with OTP")
          )}
        </Button>
      </Box>
      <ShareboxFooter />
    </Box>
  );
}
